import { HBMenuItemProps, IMenuItem } from '../HBMenu/HBMenu.types';
import React, { useMemo } from 'react';
import { uniqueId } from 'lodash-es';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import HBListItem from '../HBListItem';
import HBTooltip from '../HBTooltip/HBTooltip';
import { Z_INDEX } from '../../styles/variables';
import {
    dropdownReferenceTypesMap,
    getHBListItemSize,
    getHBListItemTheme
} from './HBMenu.constants';
import { StyledIcon } from './HBMenu.styles';

const HBMenuItem = ({
    item,
    size,
    dropdownReferenceType = 'item',
    isListItemContentFullWidth,
    ...props
}: HBMenuItemProps) => {
    const showRightIcon =
        (item.setTextBeforeIcon && item.icon) ||
        item.rightIcon ||
        item.isSelected;

    const { testId, testIdName } = item;
    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'menu-item',
        componentName: 'HBMenuItem'
    });

    const tooltipId = useMemo(
        () => uniqueId(`${finalTestId || 'menu-item'}-tooltip-`),
        [finalTestId]
    );

    const renderWrapper = (children: React.ReactNode) => {
        const content = (item as IMenuItem).itemWrapper
            ? (item as IMenuItem).itemWrapper({ children })
            : children;

        const hasTooltipLocal =
            item.tooltip && !(item as IMenuItem).itemWrapper;

        if (hasTooltipLocal) {
            const {
                content: tooltipContent,
                placement = 'right',
                offsetX: offsetXFromProps,
                customStyle,
                ...tooltipProps
            } = item.tooltip!;

            const offsetX =
                offsetXFromProps ??
                (placement === 'left' || placement === 'right' ? 16 : 0);

            return (
                <>
                    <HBTooltip
                        target={content}
                        placement={placement}
                        offsetX={offsetX}
                        openTrigger="hover"
                        openOnFocus={true}
                        customStyle={{
                            zIndex: Z_INDEX.MENU + 1,
                            ...customStyle
                        }}
                        {...tooltipProps}
                    >
                        {tooltipContent}
                    </HBTooltip>
                    <VisuallyHidden.Root id={tooltipId}>
                        {tooltipContent}
                    </VisuallyHidden.Root>
                </>
            );
        }

        return <>{content}</>;
    };

    const hasTooltip = !!item.tooltip;
    const useNativeDisabled = item.disabled && !hasTooltip;
    const isMulti = item.selectable === 'multi';
    const isSingle = item.selectable === 'single';
    const isSelectable = isMulti || isSingle;

    // Use Radix's CheckboxItem for multi-select — it natively handles checked
    // toggling while preserving focus on the item. Use regular Item otherwise.
    const resolvedReferenceType = isMulti
        ? 'multiSelect'
        : dropdownReferenceType;
    const DropdownComponent = dropdownReferenceTypesMap[resolvedReferenceType];

    const multiProps = isMulti
        ? {
              checked: !!item.isSelected,
              onCheckedChange: (checked: boolean) => {
                  if (item.onSelectionChange) {
                      item.onSelectionChange(checked);
                  } else {
                      item.onClick?.(
                          checked as unknown as React.MouseEvent<HTMLDivElement>
                      );
                  }
              }
          }
        : {};

    // Selection is driven by:
    // - CheckboxItem.onCheckedChange for multi (preserves focus)
    // - onSelect for single items that use onSelectionChange
    // - HBActionArea (synchronous click path) for single items that use onClick only
    const onSelectHandler = ((e: Event) => {
        if (item.disabled) {
            e.preventDefault();
            return;
        }
        if (isMulti) {
            // CheckboxItem handles selection via onCheckedChange; just keep open
            e.preventDefault();
            return;
        }
        if (isSingle && item.onSelectionChange) {
            // onSelectionChange is the typed API for single-select. Call it here
            // inside Radix's onSelect so the menu can close after this fires.
            item.onSelectionChange(!item.isSelected);
            return;
        }
        // All other cases (non-selectable items, and isSingle items that use
        // onClick instead of onSelectionChange):
        // onClick is already handled synchronously by HBListItem's HBActionArea
        // before flushSync commits state. Calling it here a second time would
        // either double-fire (non-selectable) or run inside flushSync after the
        // menu has closed, leaving consumer store/context references in a
        // torn-down state and causing the callback to receive undefined instead
        // of the expected item value. Let Radix close the menu via its default
        // behavior; nothing else needed.
    }) as unknown as ((event: Event) => void) &
        React.ReactEventHandler<HTMLDivElement>;

    // For single-select items that rely on onClick (no onSelectionChange), the
    // click must be routed through HBActionArea synchronously — before Radix's
    // flushSync boundary — so consumer callbacks still have access to live
    // store/context references. Use a no-op only when onSelectionChange is the
    // selection driver (it runs from onSelectHandler above) or for multi-select
    // (onCheckedChange drives that path).
    const useNoOpClick = (isSingle && !!item.onSelectionChange) || isMulti;

    return renderWrapper(
        <DropdownComponent
            {...multiProps}
            disabled={useNativeDisabled ? true : undefined}
            aria-disabled={item.disabled ? true : undefined}
            aria-describedby={hasTooltip ? tooltipId : undefined}
            asChild
            onClick={
                item.disabled
                    ? (e: React.MouseEvent<HTMLDivElement>) =>
                          e.preventDefault()
                    : undefined
            }
            onSelect={onSelectHandler}
        >
            <HBListItem
                {...(!item.disabled &&
                    item?.onClick && {
                        // For multi-select and single-select items driven by
                        // onSelectionChange, onClick is a no-op — selection runs
                        // via onCheckedChange / onSelectHandler above. The no-op
                        // keeps HBActionArea rendered so Radix's click/keyboard
                        // events have a focusable target.
                        // For single-select items that use onClick (no
                        // onSelectionChange), the real onClick must fire
                        // synchronously here — before Radix's flushSync boundary
                        // — so consumer store/context references are still live.
                        onClick: useNoOpClick
                            ? () => {}
                            : e => {
                                  e.stopPropagation();
                                  item?.onClick?.(e);
                              }
                    })}
                selected={item.isSelected}
                selectable={item.selectable}
                disabled={item.disabled}
                theme={getHBListItemTheme(item.theme)}
                size={getHBListItemSize(size)}
                subtext={item.description}
                isFullWidthContent={isListItemContentFullWidth}
                prefix={
                    !item.setTextBeforeIcon &&
                    item.icon && (
                        <StyledIcon
                            data-set-default-fill-color={
                                item.shouldUseIconDefaultFill
                            }
                        >
                            {item.icon}
                        </StyledIcon>
                    )
                }
                suffix={
                    showRightIcon &&
                    (item.rightIcon ||
                        (item.setTextBeforeIcon && item.icon)) && (
                        <StyledIcon
                            data-set-default-fill-color={
                                item.shouldUseIconDefaultFill
                            }
                        >
                            {item.rightIcon || item.icon}
                        </StyledIcon>
                    )
                }
                data-testid={finalTestId}
                {...props}
            >
                {item.title}
            </HBListItem>
        </DropdownComponent>
    );
};

export default HBMenuItem;
